/**
 * MCR (Mahjong Competition Rules) ruleset.
 *
 * Phase 1 of this ruleset: the engine skeleton and the real settlement math
 * (see payout.ts), registered and fully testable, but with NO pattern
 * detection yet — `score()` always returns 0 points. That's deliberate, not
 * an oversight: implementing all 81 fan patterns (plus the exclusion rules
 * many of them carry) is a separate, much larger follow-up. Until then this
 * ruleset can never legally produce a real win (every hand falls below
 * minimumPoints — settling as a False Hu in Serious mode, or a no-op in Fun
 * mode) — see settings.ts and payout.ts for what already works, including
 * the Fun/Serious mode split (added 2026-09-22, not part of the official
 * rulebook — see settings.ts's own doc comment).
 *
 * Also unlike HKOS, this isn't wired into the player-facing UI yet
 * (Setup screen, mode badge, settlement-explanation text) — those all
 * currently assume HkosSettings. Wiring MCR into them is deferred until
 * there's something real for a player to see happen when they pick it.
 *
 * `validateHand` here is deliberately narrow: just the tile-shape checks
 * that apply regardless of ruleset (pool legality, tile count vs. declared
 * kongs, meld sizes, the winning tile actually being in hand). It does NOT
 * share code with HKOS's `validateHand` — HKOS's version also covers a pile
 * of HKOS-specific circumstances (kong replacement, robbing a kong, the
 * heavenly/earthly/last-wall-tile/last-discard family) that don't yet have
 * an MCR equivalent, since MCR has no pattern detection yet to make any of
 * them scoreable. Sharing now would mean guessing which parts of HKOS's
 * validation are genuinely ruleset-agnostic versus HKOS-specific before
 * MCR's own patterns exist to settle the question — revisit once Phase 2
 * (patterns) gives a real answer.
 */

import type {
  DrawContext,
  HandInput,
  Payment,
  PlayerId,
  Ruleset,
  ScoreResult,
  WinContext,
} from '../types';
import { validateTilePool } from '../tiles';
import { decompose } from '../shared/decompose';
import { settleDrawRound, settleWin } from './payout';
import { activeMinimumPoints, DEFAULT_SETTINGS, type McrSettings } from './settings';

export * from './settings';

function validateHand(hand: HandInput, _ctx: WinContext, _settings: McrSettings): string[] {
  const problems = validateTilePool(allTiles(hand));

  const kongs = hand.melds.filter((m) => m.type === 'kong').length;
  const expected = 14 + kongs; // each kong adds one extra tile to the hand
  const actual = allTiles(hand).length;
  if (actual !== expected) {
    problems.push(
      `Hand has ${actual} tiles; expected ${expected} (14 + ${kongs} kong${kongs === 1 ? '' : 's'})`,
    );
  }

  for (const m of hand.melds) {
    const size = m.type === 'kong' ? 4 : m.type === 'pair' ? 2 : 3;
    if (m.tiles.length !== size) {
      problems.push(`${m.type} meld has ${m.tiles.length} tiles, expected ${size}`);
    }
  }

  if (!hand.concealed.includes(hand.winningTile)) {
    problems.push(
      `Winning tile ${hand.winningTile} must be present in the concealed tiles`,
    );
  }

  return problems;
}

function allTiles(hand: HandInput) {
  return [...hand.concealed, ...hand.melds.flatMap((m) => m.tiles)];
}

/**
 * Shape-only check, no settings needed. Phase 1 only recognises the
 * standard 4-sets-and-a-pair shape — Seven Pairs, Seven Shifted Pairs, and
 * Thirteen Orphans are all valid MCR shapes too, but recognising them
 * belongs with their pattern-detection work in a later phase, not here.
 */
function isCompleteHand(hand: HandInput): boolean {
  return decompose(hand.concealed, hand.melds).length > 0;
}

/**
 * No pattern detection yet (see module header) — every hand scores 0 Basic
 * Points, which is below any sane minimumPoints setting, so `settle()`
 * always takes the False Hu branch for now. That's a correct, honest
 * reflection of "this ruleset can't yet recognise a legal win," not a bug.
 */
function scoreHand(_hand: HandInput, _ctx: WinContext, settings: McrSettings): ScoreResult {
  const basic = 0;
  return {
    fan: basic,
    effectiveFan: basic,
    patterns: [],
    isLimit: false,
    meetsMinimum: basic >= activeMinimumPoints(settings),
    basepoints: basic,
    notes: ['MCR pattern detection is not implemented yet — every hand currently scores 0 points.'],
  };
}

export const mcr: Ruleset<McrSettings> = {
  id: 'mcr',
  name: 'MCR (Mahjong Competition Rules)',
  defaultSettings: DEFAULT_SETTINGS,
  validate: validateHand,
  isCompleteHand,
  score: scoreHand,
  settle: (
    result: ScoreResult,
    ctx: WinContext,
    players: PlayerId[],
    settings: McrSettings,
  ): Payment[] => settleWin(result, ctx, players, settings),
  settleDraw: (ctx: DrawContext, players: PlayerId[], _settings: McrSettings): Payment[] =>
    settleDrawRound(ctx, players),
};
